import { Action, Mutation, Module, RegisterOptions } from "vuex-class-modules";
import Axios, { AxiosResponse } from "axios";
import { get, set, clear } from "idb-keyval";
import {
    AlarmStatus,
    ILocationFromDatabase,
    ILocationFromParticle,
    IMiscInfo,
} from "@/models/AlarmStatus";
import AxiosSecureModule from "./axiosSecureModule";
import { app, sensor } from "@/store";
import { Log } from "@/plugins/Logging";
import { configureRouter } from "@/router";
import VueRouter from "vue-router";
import useDevice from "@/utilities/useDevice";

@Module({ generateMutationSetters: true })
export default class AlarmModule extends AxiosSecureModule {
    // --- State (sensor-related booleans removed; door/hatch handled by SensorModule) ---
    public alarmStatus: AlarmStatus | null = null;
    public armed = false;
    public siren = false;
    public syncStatus = false;
    public syncAlarm = false;
    public syncSiren = false;

    public trackingStatus = false;
    public catchThiefTimeout: any = null;
    public seconds = 0;
    public secondsSinceLastAlarm: number | null = null;

    public lastMiscInfo: IMiscInfo | null = null;
    public lastKnownLocation: ILocationFromParticle | null = null;
    public lat: number | null = null;
    public lng: number | null = null;
    public user_lat: number | null = null;
    public user_lng: number | null = null;

    // Enrollment & alarm UX
    public currentSensorConnected = false;
    public pumpIsRunning = false;
    public gasSensorConnected = false;
    public gasIsAlarm = false;
    settings: any;
    public gpsRadius = 100;
    public inAlarmMode = false;
    public deviceThatTriggeredAlarm = 0;
    public alarmReason = "";
    public alreadyLoggedIn = false;
    public userLocReady = false;
    public sirenDuration: number = 240;
    public isSilenceSirenAvailable: boolean = false;

    // Silence window countdown state
    public silenceRemaining: number = 0; // seconds remaining in the window
    public silenceTimer: any = null; // interval id

    constructor(options: RegisterOptions) {
        super(options);
        get("armed").then((value) => {
            if (value) this.setArmed(value);
            else this.setArmed(false);
        });
        get("status").then((value) => {
            if (value) this.setStatus(value);
        });
    }

    // --- Actions ---
    @Action
    public async clearAlarmData() {
        clear().then(() => {
            this.alarmStatus = null;
        });
    }

    @Action
    public async getData() {
        await this.defineHeaderAxios();
        Axios.get("api/Alarm")
            .then((result: AxiosResponse) => {
                this.setData(result.data);
            })
            .catch((error) => {
                Log.error("Get alarm data failed.", error);
            });
    }

    @Action
    public async reset() {
        await this.defineHeaderAxios();
        Axios.post("api/Alarm/reset").catch((error) => {
            Log.error("Reset failed.", error);
            app.error = "Factory reset failed";
        });
    }

    @Action
    public async restart() {
        await this.defineHeaderAxios();
        Axios.post("api/Alarm/restart").catch((error) => {
            Log.error("Restart failed.", error);
            app.error = "Device restart failed";
        });
    }

    @Action
    public async getStatus() {
        await this.defineHeaderAxios();
        Axios.get(`api/Alarm/status`)
            .then(() => Log.information("Status called."))
            .catch((error) => {
                this.syncAlarm = false;
                Log.error("Status call failed.", error);
            });
    }

    @Action
    public async arm() {
        this.startAlarmSync();
        await this.defineHeaderAxios();
        try {
            const response = await Axios.get(`api/Alarm/Arm`);
            Log.information("Arm succeeded.");
        } catch (error: any) {
            this.syncAlarm = false;
            const serverMessage = error?.response?.data;
            const hasServerMsg = typeof serverMessage === "string" && serverMessage.trim().length > 0;
            Log.error("Arm failed.", error);
            // Rewrite server message if present

            app.error = hasServerMsg ? `Arming failed. ${serverMessage}` : "Arming failed.";
        }
    }

    @Action
    public async disarm() {
        this.startAlarmSync();
        await this.defineHeaderAxios();
        Axios.get(`api/Alarm/Disarm`).catch((error) => {
            this.syncAlarm = false;
            Log.error("Disarm failed.", error);
            app.error = "Disarming failed";
        });
    }

    @Action
    public async stopSiren() {
        this.startSirenSync();
        await this.defineHeaderAxios();
        Axios.get(`api/Alarm/StopSiren`).catch((error) => {
            Log.error("Stopping siren failed.", error);
            app.error = "Stopping siren failed";
        });
    }

    private startAlarmSync() {
        this.syncAlarm = true;
        this.seconds = 0;
        const intervalID = setInterval(() => {
            if (this.syncAlarm) {
                if (this.seconds >= 60) {
                    app.error = "Device not reachable within the last minute. Possibly offline.";
                    this.syncAlarm = false;
                } else {
                    this.seconds++;
                }
            } else {
                clearInterval(intervalID);
            }
        }, 1000);
    }

    private startSirenSync() {
        this.syncSiren = true;
        this.seconds = 0;
        const intervalID = setInterval(() => {
            if (this.syncSiren) {
                if (this.seconds >= 60) {
                    app.error = "Device not reachable within the last minute. Possibly offline.";
                    this.syncSiren = false;
                } else {
                    this.seconds++;
                }
            } else {
                clearInterval(intervalID);
            }
        }, 1000);
    }

    // Silence window: start countdown for given seconds (default = sirenDuration)
    @Action
    public async startSilenceWindow(startSeconds?: number) {
        const total = startSeconds != null ? Math.max(0, Math.floor(startSeconds)) : this.sirenDuration;
        // Reset existing timer and set remaining
        this.clearSilenceTimer();
        this.setSilenceRemaining(total);
        if (total <= 0) return;

        this.silenceTimer = setInterval(() => {
            const next = Math.max(0, (this.silenceRemaining || 0) - 1);
            this.setSilenceRemaining(next);
            if (next <= 0) {
                this.clearSilenceTimer();
            }
        }, 1000);
    }

    @Action
    public async fetchSecondsSinceLastAlarm() {
        await this.defineHeaderAxios();
        try {
            const response = await Axios.get("api/sensor/SecondsSinceLastAlarm");
            const secs: number | null = response.data;
            this.setSecondsSinceLastAlarm(secs);
            if (secs == null) {
                // Unknown -> don't show
                this.clearSilenceTimer();
                this.setSilenceRemaining(0);
            } else {
                const remaining = Math.max(0, this.sirenDuration - secs);
                if (remaining > 0) {
                    await this.startSilenceWindow(remaining);
                } else {
                    this.clearSilenceTimer();
                    this.setSilenceRemaining(0);
                }
            }
        } catch (error) {
            Log.error("Failed to fetch seconds since last alarm.", error);
            this.setSecondsSinceLastAlarm(null);
            this.clearSilenceTimer();
            this.setSilenceRemaining(0);
        }
    }

    @Action
    public startTracking() {
        this.trackingStatus = true;
        Log.information("Tracking started.");
    }

    @Action
    public stopTracking() {
        this.trackingStatus = false;
        Log.information("Tracking stopped.");
    }

    @Action
    public async catchThiefStart(count: number = 20) {
        // send to server -> server will convert to 2-byte hex for Particle
        await this.defineHeaderAxios();
        await Axios.post(`api/Alarm/CatchThiefStart/${count}`);

        // flip UI tracking on
        this.startTracking();

        // auto-stop after count * 30s
        if (this.catchThiefTimeout) {
            clearTimeout(this.catchThiefTimeout);
        }
        this.catchThiefTimeout = setTimeout(() => {
            this.stopTracking(); // UI-only stop; device stops by itself after count cycles
            this.catchThiefTimeout = null;
        }, count * 30_000);
    }

    @Action
    public async catchThiefStop() {
        await this.defineHeaderAxios();
        await Axios.post(`api/Alarm/CatchThiefStop`);
        // stop UI state immediately
        if (this.catchThiefTimeout) {
            clearTimeout(this.catchThiefTimeout);
            this.catchThiefTimeout = null;
        }
        this.stopTracking();
    }

    @Action
    public async setGasThreshold(threshold: string) {
        await this.defineHeaderAxios();
        Axios.post(`api/Alarm/SetGasThreshold/${threshold}`)
            .then(() => {
                Log.information("Set gas threshold to " + threshold);
            })
            .catch((error) => {
                Log.error("Set gas threshold failed.", error);
            });
    }

    @Action
    public async setBilgeThreshold(threshold: string) {
        await this.defineHeaderAxios();
        Axios.post(`api/Alarm/SetBilgeThreshold/${threshold}`)
            .then(() => {
                Log.information("Set bilge threshold to " + threshold);
            })
            .catch((error) => {
                Log.error("Set bilge threshold failed.", error);
            });
    }

    @Action
    public async getGeoLocation() {
        await this.defineHeaderAxios();
        Axios.get(`api/Alarm/geo`)
            .then((result: AxiosResponse<ILocationFromDatabase>) => {
                Log.information("Fetch geolocation from server");
                if (result.status === 200) {
                    this.lat = result.data.latitude;
                    this.lng = result.data.longitude;
                }
            })
            .catch((error) => {
                Log.error("Fetch geolocation failed.", error);
            });
    }

    @Action
    public async getGeoHistory(dateRange: string) {
        await this.defineHeaderAxios();
        Axios.get(`api/Alarm/geo/${dateRange}`)
            .then(() => Log.information("Fetch geo history from server"))
            .catch((error) => {
                Log.error("Fetch geo history failed.", error);
            });
    }

    @Action
    public async setCurrentSensorConnected(state: boolean) {
        this.currentSensorConnected = state;
    }

    @Action
    public async setPumpRunningState(state: boolean) {
        this.pumpIsRunning = state;
    }

    @Action
    public async setGasSensorConnected(state: boolean) {
        this.gasSensorConnected = state;
    }

    @Action
    public async setGasAlarmState(state: boolean) {
        this.gasIsAlarm = state;
        //if (state) {
        //    this.SetInAlarmMode(true);
        //    this.SetAlarmModeTriggered(true);
        //    Log.information("Gas alarm triggered.");
        //} else {
        //    this.SetInAlarmMode(false);
        //    this.SetAlarmModeTriggered(false);
        //    Log.information("Gas alarm cleared.");
        //}
    }

    // --- Mutations ---
    @Mutation
    public setInAlarmMode(value: boolean) {
        this.inAlarmMode = value;
    }

    @Mutation
    public setAlarmModeTriggered(value: boolean) {
        this.inAlarmMode = value;
        let alarmSource = useDevice().getName(this.deviceThatTriggeredAlarm).toUpperCase();
        // Special case for bilge pump
        if (this.deviceThatTriggeredAlarm === 17) {
            this.setAlarmReason("bilge");
        }
        switch (this.alarmReason) {
            case "alarm":
                app.alarm = alarmSource;
                break;
            case "age":
            case "bilge":
                app.alert = alarmSource;
                break;
            default:
                app.alarm = alarmSource;
                break;
        }
    }
    @Mutation
    public setAlarmOrigin(device: number) {
        this.deviceThatTriggeredAlarm = device;
    }
    @Mutation
    public setAlarmReason(reason: string) {
        this.alarmReason = reason;
    }
    @Mutation
    public setData(data: any) {
        this.settings = data;
    }

    @Mutation
    public setStatus(data: AlarmStatus | null) {
        this.syncStatus = false;
        if (data) {
            this.alarmStatus = data;
            if (data !== null && data.Alarm) {
                this.armed = data.Alarm.trim() === "1";
                this.syncAlarm = false;
                set("armed", this.armed);
            }
            this.alarmStatus = data;
            if (!this.alreadyLoggedIn) {
                this.alreadyLoggedIn = true;
                configureRouter().then((router: VueRouter) => {
                    router.replace({ name: "Home" }).catch(() => {
                        Log.information("Logged in successfully.");
                    });
                });
            }
        }
    }

    @Mutation
    public setArmed(data: any) {
        this.armed = data;
        this.syncAlarm = false;
        set("armed", this.armed);
        if (!this.armed) {
            this.inAlarmMode = false;
        }
    }

    @Mutation
    public setSiren(data: any) {
        this.siren = data;
        this.syncSiren = false;
        set("siren", this.siren);
    }

    @Mutation
    public setSirenDuration(value: number) {
        this.sirenDuration = value;
    }

    @Mutation
    public setCurrentLocation(location: ILocationFromParticle) {
        this.lastKnownLocation = location;
        this.lat = location.location.geometry.coordinates[1];
        this.lng = location.location.geometry.coordinates[0];
    }

    @Mutation
    public setUserLocation(location: GeolocationPosition) {
        this.user_lat = location.coords.latitude;
        this.user_lng = location.coords.longitude;
        this.userLocReady = true;
    }

    @Mutation
    public setCurentMiscInfo(miscInfo: IMiscInfo) {
        this.lastMiscInfo = miscInfo;
    }

    @Mutation
    public setSecondsSinceLastAlarm(value: number | null) {
        this.secondsSinceLastAlarm = value;
        // Do not set isSilence here; countdown controls it
    }

    @Mutation
    public setSilenceRemaining(value: number) {
        this.silenceRemaining = value;
        this.isSilenceSirenAvailable = value > 0;
    }

    @Mutation
    public clearSilenceTimer() {
        if (this.silenceTimer) {
            clearInterval(this.silenceTimer);
            this.silenceTimer = null;
        }
    }
}